import { useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { formatDate, isValidPhone } from "@njbsoft/utils";
import {
  useAccount,
  useUpdateAccount,
  useDeleteAccount,
  useAccountPremises,
  useAccountDeviceInstallations,
  useAccountMeters,
  useAccountBilledInstallations,
} from "@/hooks/use-account";
import { useAppStore } from "@/store";
import { useInlineField } from "@/hooks/use-inline-field";
import { usePermissions } from "@/hooks/use-permissions";
import { useOrgComplianceSettings } from "@/hooks/use-org-compliance-settings";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { useNeedsAttentionRollup, rollupToMap } from "@/hooks/use-needs-attention-rollup";
import { useCustomFieldDefinitions } from "@/hooks/use-custom-field-definitions";
import { usePatchAccountCustomFields } from "@/hooks/use-account-custom-fields";
import { toDatetimeLocalValue, fromDatetimeLocalValue } from "@/lib/dates";
import { AccountPremisesTable } from "@/components/accounts/account-premises-table";
import { AccountInstallationsTable } from "@/components/accounts/account-installations-table";
import { AccountBillingTable } from "@/components/accounts/account-billing-table";
import { AccountMetersTable } from "@/components/accounts/account-meters-table";
import { AccountMetaRow } from "@/components/accounts/account-meta-row";
import { accountTypes, noticeChannels } from "@/components/accounts/account-form";
import {
  accountDeleteCopy,
  type AccountDeleteEnvelope,
} from "@/components/accounts/account-delete-copy";
import { PortalAccessCard } from "@/components/accounts/portal-access-card";
import { AuthorizationCodeCard } from "@/components/accounts/authorization-code-card";
import { NoticeHistoryCard } from "@/components/notices/notice-history-card";
import { CustomFieldsCard } from "@/components/custom-fields/custom-fields-card";
import {
  PageHeader,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  PageSkeleton,
  Tabs,
  type TabDef,
  EmptyValue,
  NeedsAttentionFlag,
  InlineEditable,
  InlineTextEditor,
  InlineSelectEditor,
  InlineDateTimeEditor,
  InlineBlockEditor,
} from "@/components/ui";
import { MailIcon } from "@/components/icons";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { RelatedTasksPanel } from "@/components/tasks/related-tasks-panel";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { NotesCard } from "@/components/entity-notes/notes-card";

type AccountUpdate = components["schemas"]["AccountUpdate"];

type AccountTab =
  | "overview"
  | "premises"
  | "meters"
  | "device-installations"
  | "billing"
  | "notices"
  | "tasks"
  | "documents";

const ALLOWED_TABS: AccountTab[] = [
  "overview",
  "premises",
  "meters",
  "device-installations",
  "billing",
  "notices",
  "tasks",
  "documents",
];

interface AccountDetailSearch {
  tab?: AccountTab;
}

function parseTab(value: unknown): AccountTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/accounts/$id/")({
  component: AccountDetailPage,
  validateSearch: (s: Record<string, unknown>): AccountDetailSearch => ({
    tab: parseTab(s.tab),
  }),
});

function AccountDetailPage() {
  const { t } = useTranslation(["accounts", "common", "notices"]);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const toasts = useToast();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: account, isLoading, isError } = useAccount(id);
  const { data: complianceSettings } = useOrgComplianceSettings(orgId);
  useRecentPageTitle(account?.name ?? undefined);
  // Lightweight count-only queries for the tab labels. Each fetches a
  // single row (limit=1) and reads the server-side `total` projection;
  // no rows are surfaced to the route's render layer (the tab bodies
  // mount their own full-pagination queries when active).
  const premisesCountQuery = useAccountPremises(id, { limit: 1 });
  const installationsCountQuery = useAccountDeviceInstallations(id, { limit: 1 });
  const metersCountQuery = useAccountMeters(id, { limit: 1 });
  const billedCountQuery = useAccountBilledInstallations(id, { limit: 1 });
  const customFieldDefinitions = useCustomFieldDefinitions("account");
  const { patchValues: patchCustomFields } = usePatchAccountCustomFields(id);
  const deleteMutation = useDeleteAccount();
  const updateMutation = useUpdateAccount(id);
  const patchField = useInlineField<AccountUpdate>((body) => updateMutation.mutateAsync(body), {
    successKey: "accounts:updated",
    errorKeyByCode: {
      account_number_conflict: "accounts:errors.accountNumberConflict",
      account_record_immutable_post_soft_delete: "accounts:errors.recordImmutablePostSoftDelete",
    },
  });
  const { data: naRollup } = useNeedsAttentionRollup("account", [id]);
  const naItem = rollupToMap(naRollup).get(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteEnvelope, setDeleteEnvelope] = useState<AccountDeleteEnvelope | null>(null);

  const archived = !!account?.deleted_at;
  // Archived rows are immutable; everyone else edits inline only with the
  // account-update grant (the facility detail gates its inline fields the same
  // way on `facilities:update`).
  const fieldsEditable = !archived && can("accounts", "update");

  // Re-validated at read time, not just in validateSearch, so an unknown tab in
  // a hand-typed or stale URL still lands on the default body.
  const activeTab: AccountTab = parseTab(tab) ?? "overview";
  const setActiveTab = (next: AccountTab) => {
    void navigate({
      to: "/accounts/$id",
      params: { id },
      search: next === "overview" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !account) {
    return (
      <div className="p-8">
        <Breadcrumbs items={[{ label: t("list.title"), to: "/accounts" }]} />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  // The count probes keep the previous page while a new key loads, so on an
  // account switch their data is the previous account's until the new probe
  // lands. A placeholder total is never this account's, so it shows no count.
  const countOf = (q: { data?: { total: number }; isPlaceholderData?: boolean }) =>
    q.isPlaceholderData ? undefined : q.data?.total;

  const tabs: TabDef<AccountTab>[] = [
    { value: "overview", label: t("detail.tabOverview") },
    {
      value: "premises",
      label: t("detail.tabPremises"),
      count: countOf(premisesCountQuery),
    },
    {
      value: "meters",
      label: t("detail.tabMeters"),
      count: countOf(metersCountQuery),
    },
    {
      value: "device-installations",
      label: t("detail.tabDeviceInstallations"),
      count: countOf(installationsCountQuery),
    },
    {
      value: "billing",
      label: t("detail.tabBilling"),
      count: countOf(billedCountQuery),
    },
    { value: "notices", label: t("detail.tabNotices") },
    { value: "tasks", label: t("tasks:relatedPanel.title") },
    { value: "documents", label: t("documents:page.title") },
  ];

  // Service state (closed_at) is a DIFFERENT axis from the archive state
  // (deleted_at): a closed account is still a live record, and it is the state
  // the escalation ladder's account_closed stop reads.
  const closed = !!account.closed_at;
  // The authorization-code card shows only once the settings read has landed
  // with the org's switch on (loading and the no-row 404 both read off) and
  // only for an open, live account: a closed account may never be handed a
  // code, and an archived one refuses every card verb.
  const showAuthorizationCode =
    complianceSettings?.account_codes_enabled === true && !archived && !closed;
  const deleteCopy = accountDeleteCopy(deleteEnvelope, account.name);

  const handleDeleteClick = () => {
    setDeleteEnvelope(null);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toasts.success("accounts:delete.success");
        setConfirmOpen(false);
        void navigate({ to: "/accounts" });
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setDeleteEnvelope((err.details as AccountDeleteEnvelope | undefined) ?? {});
        } else {
          toasts.error("accounts:errors.deleteFailed");
        }
      },
    });
  };

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("list.title"), to: "/accounts" }, { label: account.name }]} />
      <PageHeader
        title={
          <InlineEditable
            label={t("fields.name")}
            disabled={!fieldsEditable}
            display={<span>{account.name}</span>}
          >
            {(close) => (
              <InlineTextEditor
                ariaLabel={t("fields.name")}
                initial={account.name}
                close={close}
                onSubmit={(v) => {
                  if (v.trim()) void patchField({ name: v.trim() });
                }}
              />
            )}
          </InlineEditable>
        }
        meta={<AccountMetaRow account={account} />}
        actions={
          <div className="flex items-center gap-2">
            <NeedsAttentionFlag
              count={naItem?.total}
              pendingCount={naItem?.pending_review}
              onClick={() =>
                void navigate({
                  to: "/backflow/out-of-compliance",
                  search: { account_id: id },
                })
              }
            />
            {archived ? (
              <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                {t("detail.deletedBadge")}
              </span>
            ) : (
              <>
                {can("notices", "create") && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void navigate({ to: "/notices/new", search: { account_id: id } })
                    }
                  >
                    <MailIcon className="size-4" />
                    {t("notices:letters.generate")}
                  </Button>
                )}
                <Button variant="danger" onClick={handleDeleteClick}>
                  {t("detail.deleteCta")}
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="mt-4">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("entity.singular")}
        />
        <div className="mt-6" role="tabpanel">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <Card title={t("sections.accountDetails")}>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label={t("fields.accountNumber")}>
                    <InlineEditable
                      label={t("fields.accountNumber")}
                      disabled={!fieldsEditable}
                      display={account.account_number ?? <EmptyValue />}
                    >
                      {(close) => (
                        <InlineTextEditor
                          ariaLabel={t("fields.accountNumber")}
                          initial={account.account_number ?? ""}
                          close={close}
                          onSubmit={(v) => patchField({ account_number: v === "" ? null : v })}
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.accountType")}>
                    <InlineEditable
                      label={t("fields.accountType")}
                      disabled={!fieldsEditable}
                      display={t(`accountType.${account.account_type}`)}
                    >
                      {(close) => (
                        <InlineSelectEditor
                          ariaLabel={t("fields.accountType")}
                          close={close}
                          initial={account.account_type}
                          options={accountTypes.map((at) => ({
                            value: at,
                            label: t(`accountType.${at}`),
                          }))}
                          onSubmit={(v) =>
                            // v comes from the accountTypes option list, so narrowing the
                            // editor's string callback to the enum is safe.
                            patchField({ account_type: v as AccountUpdate["account_type"] })
                          }
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("serviceStatus.label")}>
                    <InlineEditable
                      label={t("serviceStatus.label")}
                      disabled={!fieldsEditable}
                      display={
                        <span className="block">
                          <span className="inline-flex items-center gap-2">
                            <Badge variant={closed ? "danger" : "success"}>
                              {closed ? t("serviceStatus.closed") : t("serviceStatus.open")}
                            </Badge>
                            {closed && account.closed_at ? formatDate(account.closed_at) : null}
                          </span>
                          {closed && (
                            <span className="mt-1 block text-xs text-muted">
                              {t("serviceStatus.effectClosed")}
                            </span>
                          )}
                        </span>
                      }
                    >
                      {(close) => (
                        <InlineDateTimeEditor
                          ariaLabel={t("fields.closedAt")}
                          close={close}
                          initial={toDatetimeLocalValue(account.closed_at)}
                          onSubmit={(v) => patchField({ closed_at: fromDatetimeLocalValue(v) })}
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.contactEmail")}>
                    <InlineEditable
                      label={t("fields.contactEmail")}
                      disabled={!fieldsEditable}
                      display={account.contact_email ?? <EmptyValue />}
                    >
                      {(close) => (
                        <InlineTextEditor
                          ariaLabel={t("fields.contactEmail")}
                          initial={account.contact_email ?? ""}
                          close={close}
                          onSubmit={(v) => patchField({ contact_email: v === "" ? null : v })}
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.contactPhone")}>
                    <InlineEditable
                      label={t("fields.contactPhone")}
                      disabled={!fieldsEditable}
                      display={account.contact_phone ?? <EmptyValue />}
                    >
                      {(close) => (
                        <InlineTextEditor
                          ariaLabel={t("fields.contactPhone")}
                          initial={account.contact_phone ?? ""}
                          close={close}
                          onSubmit={(v) => {
                            if (v !== "" && !isValidPhone(v)) {
                              toasts.error("common:validation.invalidPhone");
                              return;
                            }
                            void patchField({ contact_phone: v === "" ? null : v });
                          }}
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.preferredNoticeChannel")}>
                    <InlineEditable
                      label={t("fields.preferredNoticeChannel")}
                      disabled={!fieldsEditable}
                      display={
                        account.preferred_notice_channel ? (
                          <Badge variant="info">
                            {t(`noticeChannel.${account.preferred_notice_channel}`)}
                          </Badge>
                        ) : (
                          <EmptyValue />
                        )
                      }
                    >
                      {(close) => (
                        <InlineSelectEditor
                          ariaLabel={t("fields.preferredNoticeChannel")}
                          close={close}
                          initial={account.preferred_notice_channel ?? ""}
                          options={[
                            { value: "", label: "—" },
                            ...noticeChannels.map((c) => ({
                              value: c,
                              label: t(`noticeChannel.${c}`),
                            })),
                          ]}
                          onSubmit={(v) =>
                            patchField({
                              // v is "" or a member of the noticeChannels option list, so the
                              // narrowing to the nullable enum is safe.
                              preferred_notice_channel: (v === ""
                                ? null
                                : v) as AccountUpdate["preferred_notice_channel"],
                            })
                          }
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("sections.mailingAddress")}>
                    <InlineEditable
                      label={t("sections.mailingAddress")}
                      disabled={!fieldsEditable}
                      display={
                        <AddressDisplay
                          lines={[
                            account.mailing_address_line1,
                            account.mailing_address_line2,
                            joinCity(
                              account.mailing_city,
                              account.mailing_state,
                              account.mailing_postal_code,
                            ),
                          ]}
                          attention={account.mailing_attention}
                        />
                      }
                    >
                      {(close) => (
                        <InlineBlockEditor
                          close={close}
                          initial={{
                            line1: account.mailing_address_line1 ?? "",
                            line2: account.mailing_address_line2 ?? "",
                            city: account.mailing_city ?? "",
                            state: account.mailing_state ?? "",
                            postal: account.mailing_postal_code ?? "",
                            attention: account.mailing_attention ?? "",
                          }}
                          fields={[
                            { key: "line1", label: t("mailingAddressFields.addressLine1") },
                            { key: "line2", label: t("mailingAddressFields.addressLine2") },
                            { key: "city", label: t("mailingAddressFields.city") },
                            { key: "state", label: t("mailingAddressFields.state") },
                            { key: "postal", label: t("mailingAddressFields.postalCode") },
                            { key: "attention", label: t("fields.mailingAttention") },
                          ]}
                          onSubmit={(v) =>
                            patchField({
                              mailing_address_line1: v.line1 || null,
                              mailing_address_line2: v.line2 || null,
                              mailing_city: v.city || null,
                              mailing_state: v.state || null,
                              mailing_postal_code: v.postal || null,
                              mailing_attention: v.attention || null,
                            })
                          }
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("sections.billingAddress")}>
                    <InlineEditable
                      label={t("sections.billingAddress")}
                      disabled={!fieldsEditable}
                      display={
                        <AddressDisplay
                          lines={[
                            account.billing_address_line1,
                            account.billing_address_line2,
                            joinCity(
                              account.billing_city,
                              account.billing_state,
                              account.billing_postal_code,
                            ),
                          ]}
                        />
                      }
                    >
                      {(close) => (
                        <InlineBlockEditor
                          close={close}
                          initial={{
                            line1: account.billing_address_line1 ?? "",
                            line2: account.billing_address_line2 ?? "",
                            city: account.billing_city ?? "",
                            state: account.billing_state ?? "",
                            postal: account.billing_postal_code ?? "",
                          }}
                          fields={[
                            { key: "line1", label: t("billingAddressFields.addressLine1") },
                            { key: "line2", label: t("billingAddressFields.addressLine2") },
                            { key: "city", label: t("billingAddressFields.city") },
                            { key: "state", label: t("billingAddressFields.state") },
                            { key: "postal", label: t("billingAddressFields.postalCode") },
                          ]}
                          onSubmit={(v) =>
                            patchField({
                              billing_address_line1: v.line1 || null,
                              billing_address_line2: v.line2 || null,
                              billing_city: v.city || null,
                              billing_state: v.state || null,
                              billing_postal_code: v.postal || null,
                            })
                          }
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.taxId")}>
                    <InlineEditable
                      label={t("fields.taxId")}
                      disabled={!fieldsEditable}
                      display={account.tax_id ?? <EmptyValue />}
                    >
                      {(close) => (
                        <InlineTextEditor
                          ariaLabel={t("fields.taxId")}
                          initial={account.tax_id ?? ""}
                          close={close}
                          onSubmit={(v) => patchField({ tax_id: v === "" ? null : v })}
                        />
                      )}
                    </InlineEditable>
                  </Field>
                </dl>
              </Card>
              <PortalAccessCard
                accountId={id}
                contactEmail={account.contact_email ?? null}
                archived={archived}
              />
              {showAuthorizationCode && (
                <AuthorizationCodeCard accountId={id} canManage={can("accounts", "update")} />
              )}
              <div className="grid gap-4 lg:grid-cols-2">
                <CustomFieldsCard
                  definitions={customFieldDefinitions.data?.items ?? []}
                  values={account.custom_fields}
                  canEdit={fieldsEditable}
                  patchValues={patchCustomFields}
                />
              </div>
              {/* Internal notes close out the Overview column, below the org's
                  custom field definitions (if any). Appends on an archived
                  account reject server-side with the typed 409; listing stays
                  legal, so the card mounts unconditionally. */}
              <NotesCard parent="account" parentId={id} />
            </div>
          )}
          {/* Keyed by the account: a param-only navigation reuses this route,
              so without the key a table would keep the previous account's
              page, search and sort, and its keepPreviousData rows would render
              under this account's header until the new page loads. */}
          {activeTab === "premises" && <AccountPremisesTable key={id} accountId={id} />}
          {activeTab === "meters" && <AccountMetersTable key={id} accountId={id} />}
          {activeTab === "device-installations" && (
            <AccountInstallationsTable key={id} accountId={id} />
          )}
          {activeTab === "billing" && <AccountBillingTable key={id} accountId={id} />}
          {activeTab === "notices" && <NoticeHistoryCard scope="account" id={id} />}
          {activeTab === "tasks" && <RelatedTasksPanel entityType="account" entityId={id} />}
          {activeTab === "documents" && orgId && (
            <EntityDocuments entityType="account" entityId={id} orgId={orgId} />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t(deleteCopy.titleKey)}
        confirmLabel={t(deleteCopy.confirmKey)}
        cancelLabel={t("delete.cancel")}
        variant={deleteCopy.variant}
        message={t(deleteCopy.messageKey, deleteCopy.values)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-on-surface">{children}</dd>
    </div>
  );
}

function joinCity(city?: string | null, state?: string | null, postal?: string | null): string {
  return [city, state, postal].filter(Boolean).join(", ");
}

function AddressDisplay({
  lines,
  attention,
}: {
  lines: (string | null | undefined)[];
  attention?: string | null;
}) {
  const shown = lines.filter((l): l is string => !!l && l.trim() !== "");
  if (shown.length === 0 && !attention) return <EmptyValue />;
  return (
    <span className="block">
      {shown.map((l, i) => (
        <span key={i} className="block">
          {l}
        </span>
      ))}
      {attention ? <span className="block text-muted">{attention}</span> : null}
    </span>
  );
}
